import React, { useState, useEffect, useCallback } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, Alert, AlertTitle, Link, Tooltip, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PageHeader from './common/PageHeader';
import StatCard from './common/StatCard';
import ChartCard from './common/ChartCard';
import LoadingBlock from './common/LoadingBlock';
import ErrorBlock from './common/ErrorBlock';
import { getCreatorFunnel } from '../api';

// Age bands are ordered, so they get a single-hue sequential ramp (recent -> old)
// rather than categorical hues. Validated: lightness monotonic 0.89 -> 0.40,
// worst adjacent CVD deltaE 16.0. The two lightest steps fall under 3:1 against the
// surface, which is why the exact figures are always shown in the table below.
const AGE_RAMP = ['#E3D0FB', '#BE96F5', '#953EF1', '#5B1A9C'];

const AGE_BANDS = [
  { key: 'under3d', label: 'Under 3 days' },
  { key: 'd3to7',   label: '3-7 days' },
  { key: 'd7to30',  label: '7-30 days' },
  { key: 'over30d', label: 'Over 30 days' },
];

export default function CreatorFunnel() {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stageFilter, setStageFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setData(await getCreatorFunnel());
    } catch (err) {
      console.error('Error loading creator funnel:', err);
      setError('Failed to load the creator funnel report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingBlock height={420} />;
  if (error) return <ErrorBlock message={error} onRetry={load} />;
  if (!data) return null;

  const { summary, funnel, byStage, cohorts, stalled, anomalies, seeded, maturityDays } = data;
  const primary = theme.palette.primary.main;
  const anomalyTotal = Math.max(
    anomalies.approvedNoPhoto, anomalies.approvedNoBio, anomalies.approvedNoExpectations
  );

  /* ---------- Drop-off, not cumulative counts.
     Conversion here is ~98%, so plotting the running total gives five bars of
     visually identical length (3,289 vs 3,215 on a zero baseline) and hides the
     whole story. The variation lives in how many fall out at each step, so that
     is what gets plotted; the running total stays in the tooltip. ---------- */
  const dropSteps = funnel.slice(1);

  const funnelChart = {
    labels: dropSteps.map(f => f.label),
    datasets: [{
      label: 'Creators lost',
      data: dropSteps.map(f => f.droppedHere),
      backgroundColor: alpha(primary, 0.65),
      borderColor: primary,
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
      barPercentage: 0.6,
    }],
  };

  const funnelOptions = {
    indexAxis: 'y',
    responsive: true,
    plugins: {
      legend: { display: false },
      datalabels: { anchor: 'end', align: 'end', offset: 4, formatter: (v) => v.toLocaleString() },
      tooltip: {
        callbacks: {
          title: (items) => `Never reached: ${items[0].label}`,
          label: (ctx) => {
            const step = dropSteps[ctx.dataIndex];
            return [
              `${step.droppedHere.toLocaleString()} lost at this step`,
              `${step.count.toLocaleString()} got through (${step.pctOfStart}% of all signups)`,
            ];
          },
        },
      },
    },
    scales: {
      x: { beginAtZero: true, grace: '12%', ticks: { precision: 0 }, title: { display: true, text: 'Creators lost at this step' } },
      y: { grid: { display: false } },
    },
  };

  /* ---------- Stalled by stage: stacked, sequential ramp, legend required ---------- */
  const stageChart = {
    labels: byStage.map(s => s.label),
    datasets: AGE_BANDS.map((band, i) => ({
      label: band.label,
      data: byStage.map(s => s[band.key]),
      backgroundColor: AGE_RAMP[i],
      borderColor: theme.palette.background.paper,
      // 2px surface gap between adjacent stacked segments
      borderWidth: 2,
      borderRadius: 4,
      borderSkipped: false,
      barPercentage: 0.6,
    })),
  };

  const stageOptions = {
    indexAxis: 'y',
    responsive: true,
    plugins: {
      legend: { position: 'bottom' },
      // Values live in the table beneath; a number on every segment is clutter.
      datalabels: { display: false },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}` } },
    },
    scales: {
      x: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
      y: { stacked: true, grid: { display: false } },
    },
  };

  /* ---------- Cohort conversion: rate only. Never a second y-axis for volume. ---------- */
  const cohortChart = {
    labels: cohorts.map(c => c.month),
    datasets: [{
      label: 'Approved within cohort',
      data: cohorts.map(c => c.rate),
      borderColor: primary,
      backgroundColor: alpha(primary, 0.12),
      borderWidth: 2,
      pointRadius: cohorts.map(c => (c.partial ? 6 : 4)),
      // A hollow marker flags a month still too young to judge.
      pointBackgroundColor: cohorts.map(c => (c.partial ? theme.palette.background.paper : primary)),
      pointBorderColor: primary,
      pointBorderWidth: 2,
      fill: true,
      tension: 0.3,
      // A partial month's rate is not comparable to a settled one — most of its
      // signups simply have not had time to finish. Solid line for settled
      // months, dashed into any month still filling up, so the tail reads as
      // "not final" rather than as a collapse.
      segment: {
        borderDash: (ctx) => (cohorts[ctx.p1DataIndex]?.partial ? [6, 4] : undefined),
      },
    }],
  };

  const cohortOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const c = cohorts[ctx.dataIndex];
            const lines = [`${c.approved.toLocaleString()} of ${c.signups.toLocaleString()} approved (${c.rate}%)`];
            if (c.partial) {
              lines.push(c.maturedRate === null
                ? `Too recent to judge — no signup is yet ${maturityDays} days old`
                : `${c.maturedRate}% among the ${c.maturedSignups} older than ${maturityDays} days`);
            }
            return lines;
          },
        },
      },
    },
    scales: {
      y: { beginAtZero: false, suggestedMin: 50, suggestedMax: 100, ticks: { callback: (v) => `${v}%` } },
      x: { grid: { display: false } },
    },
  };

  const visibleStalled = stageFilter === 'all'
    ? stalled
    : stalled.filter(s => s.stage === stageFilter);

  return (
    <Box>
      <PageHeader
        title="Creator funnel"
        subtitle="Where creator signups stall before they are reviewable. The Approve Creators queue only shows profiles complete enough to review, so accounts that stall earlier are invisible there."
      />

      {seeded?.total > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Counts below cover the {summary.totalCreators.toLocaleString()} creators who signed up themselves</AlertTitle>
          A further <strong>{seeded.total.toLocaleString()}</strong> seeded &ldquo;coming soon&rdquo; profiles were
          migrated from staging already approved and biddable, and {seeded.unclaimed.toLocaleString()} of those have
          never been claimed. They never go through onboarding, so counting them would put approval near 98% and hide
          this funnel entirely.
          {(seeded.biddableNoPhoto > 0 || seeded.biddableNoBio > 0) && (
            <>
              {' '}Note: <strong>{Math.max(seeded.biddableNoPhoto, seeded.biddableNoBio)}</strong> of them are live and
              biddable with no photo or bio.
            </>
          )}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Creators" value={summary.totalCreators.toLocaleString()} icon={<GroupsIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Approved"
            value={`${summary.conversionRate}%`}
            helpText={`${summary.approved.toLocaleString()} of ${summary.totalCreators.toLocaleString()}`}
            icon={<CheckCircleIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Not yet approved" value={summary.stalled.toLocaleString()} icon={<HourglassEmptyIcon />} color="warning" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={`Stalled over ${maturityDays} days`}
            value={summary.stalledMatured.toLocaleString()}
            helpText="Old enough that they are unlikely to finish on their own"
            icon={<WarningAmberIcon />}
            color="error"
          />
        </Grid>
      </Grid>

      {anomalyTotal > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Approved creators with an incomplete profile</AlertTitle>
          {anomalies.approvedNoPhoto > 0 && <>{anomalies.approvedNoPhoto} approved with no photo. </>}
          {anomalies.approvedNoBio > 0 && <>{anomalies.approvedNoBio} with no bio. </>}
          {anomalies.approvedNoExpectations > 0 && <>{anomalies.approvedNoExpectations} with no connection expectations. </>}
          These are live profiles missing something approval is meant to require, which is also why the funnel&rsquo;s
          final step reads slightly below the {summary.approved.toLocaleString()} total approved.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Where creators drop out"
            height={320}
            actions={
              <Typography variant="caption" color="text.secondary">
                {summary.conversionRate}% reach approval
              </Typography>
            }
          >
            <Bar data={funnelChart} options={funnelOptions} />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Approval rate by signup month" height={320}>
            <Line data={cohortChart} options={cohortOptions} />
          </ChartCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <ChartCard title="Where they are stuck, by how long" height={320}>
            <Bar data={stageChart} options={stageOptions} />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <TableContainer component={Paper} sx={{ height: '100%' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Blocked on</TableCell>
                  {AGE_BANDS.map(b => (
                    <TableCell key={b.key} align="right">{b.label.replace(' days', 'd').replace('Under ', '<').replace('Over ', '>')}</TableCell>
                  ))}
                  <TableCell align="right"><strong>Total</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byStage.map(s => (
                  <TableRow key={s.stage} hover>
                    <TableCell>{s.label}</TableCell>
                    {AGE_BANDS.map(b => (
                      <TableCell key={b.key} align="right">{s[b.key] || '—'}</TableCell>
                    ))}
                    <TableCell align="right"><strong>{s.total}</strong></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <Typography variant="h6">Creators not yet approved ({visibleStalled.length})</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={stageFilter}
            onChange={(_, v) => v && setStageFilter(v)}
          >
            <ToggleButton value="all">All</ToggleButton>
            {byStage.filter(s => s.total > 0).map(s => (
              <ToggleButton key={s.stage} value={s.stage}>{s.label.replace('No ', '').replace(' uploaded', '').replace(' written', '').replace(' set', '')}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Blocked on</TableCell>
                <TableCell align="right">Photos</TableCell>
                <TableCell align="right">Days since signup</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleStalled.map(s => (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Link component={RouterLink} to={`/user/${s.id}`} underline="hover">
                      {s.username || `User ${s.id}`}
                    </Link>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      ID {s.id}
                    </Typography>
                  </TableCell>
                  <TableCell>{s.stageLabel}</TableCell>
                  <TableCell align="right">{s.photoCount}</TableCell>
                  <TableCell align="right">
                    {s.daysSinceSignup >= 30 ? (
                      <Tooltip title="Stalled more than 30 days">
                        <Chip size="small" color="error" variant="outlined" label={`${s.daysSinceSignup}d`} />
                      </Tooltip>
                    ) : (
                      `${s.daysSinceSignup}d`
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {visibleStalled.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Nothing stalled at this stage.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Excludes deleted and test accounts. A hollow point on the monthly chart marks a cohort with signups
        younger than {maturityDays} days, whose rate will still rise.
      </Typography>
    </Box>
  );
}
